import api from './api';

// Admin > Platform > Universal API > UAPI Integrations.
//
// The code that talks to a backend system, held in the database rather than in the repo: a file tree per
// system, published as immutable versions, run by the delegation lambda with no deploy of ours. See
// UAPI-INTEGRATIONS.md.
//
// Superadmin-only, so every call is on the /admin API — the same gateway as Routes and Payloads beside it.
//
// SNAKE_CASE ON THE WIRE, deliberately: `live_version_id`, `version_id`, `uapi_access_id` are the column
// names in uapi_integrations / uapi_integration_versions / uapi_integration_candidates. Renaming a field
// in transit is how a key stops lining up between the screen, the lambda and the table.
const uapiIntegrationsService = {
    // The list, plus the systems that do NOT yet have an integration — one per system is the design, so
    // the create form offers only what can actually be claimed.
    list: () => api.get('/admin/uapi-integrations'),

    // { marketplace_id, name, description?, entry_path? }. The server also creates the draft and seeds a
    // starter file, so a new integration opens on something that already compiles.
    create: (data) => api.post('/admin/uapi-integrations', data),

    // The integration with its versions, its candidate pins, and the x-hotelids that COULD be pinned.
    get: (id) => api.get(`/admin/uapi-integrations/${id}`),

    // Partial: only the keys sent are written. `live_version_id` is the go-live — the one field here
    // that changes what every property on this system runs.
    update: (id, data) => api.patch(`/admin/uapi-integrations/${id}`, data),
    delete: (id) => api.delete(`/admin/uapi-integrations/${id}`),

    // ── files ────────────────────────────────────────────────────────────────
    // The tree (paths + sizes). versionId omitted = the draft.
    listFiles: (id, versionId) =>
        api.get(`/admin/uapi-integrations/${id}/files${versionId ? `?versionId=${versionId}` : ''}`),
    // One file WITH its content. Kept separate from the tree so opening the screen does not fetch every
    // byte of every file to draw a sidebar.
    getFile: (id, path, versionId) => api.get(`/admin/uapi-integrations/${id}/files`, {
        params: { path, ...(versionId ? { versionId } : {}) },
    }),
    saveFile: (id, path, content) => api.put(`/admin/uapi-integrations/${id}/files`, { path, content }),

    // ── the whole tree, for zip export/import ────────────────────────────────
    // The zip itself is built and unpacked in the BROWSER (jszip is already in this bundle; the util
    // layer has no zip library). These two carry the tree as { path: content } either way.
    //
    // One call each, not one per file: an export assembled from N requests can catch a save halfway and
    // produce a zip of a tree that never existed, and an import written file by file can stop halfway
    // and leave one that still doesn't.
    getTree: (id, versionId) => api.get(`/admin/uapi-integrations/${id}/files`, {
        params: { tree: 1, ...(versionId ? { versionId } : {}) },
    }),
    // mode 'replace' (default) drops anything absent from the upload — what importing a deployment
    // package means. 'merge' only adds and overwrites. Always writes the DRAFT.
    putTree: (id, files, mode) => api.put(`/admin/uapi-integrations/${id}/files`, { files, mode: mode || 'replace' }),
    renameFile: (id, from, to) => api.put(`/admin/uapi-integrations/${id}/files`, { from, to }),
    deleteFile: (id, path) => api.delete(`/admin/uapi-integrations/${id}/files`, { params: { path } }),

    // ── compile, publish, versions ───────────────────────────────────────────
    // Compiles with the loader the DISPATCHER uses. Answers 200 { ok:false, error } for a compile
    // failure — that is the answer the author asked for, not a request error.
    validate: (id, versionId) =>
        api.post(`/admin/uapi-integrations/${id}/validate`, versionId ? { version_id: versionId } : {}),
    publish: (id, notes) => api.post(`/admin/uapi-integrations/${id}/versions`, { notes }),
    restoreVersion: (id, versionId) =>
        api.post(`/admin/uapi-integrations/${id}/versions/${versionId}/restore`),
    deleteVersion: (id, versionId) => api.delete(`/admin/uapi-integrations/${id}/versions/${versionId}`),
    // Going live is an update of the integration, not an action on the version — the pointer moves, the
    // version does not change. Rollback is the same call with an older id.
    goLive: (id, versionId) => api.patch(`/admin/uapi-integrations/${id}`, { live_version_id: versionId }),
    takeOffline: (id) => api.patch(`/admin/uapi-integrations/${id}`, { live_version_id: null }),

    // ── evidence, and the model that reads it ────────────────────────────────
    // Recent requests on this system, one row per transaction. Without a transactionId this is the
    // picker; with one it is that request's steps, oldest first.
    traces: (id, transactionId) => api.get(`/admin/uapi-integrations/${id}/traces`, {
        params: transactionId ? { transactionId } : {},
    }),

    // The assistant. `focus` names what the question is about — { transaction_id, full_path } — and the
    // SERVER assembles everything else (the tree, the granted operations, the credential setting names,
    // the trace). It answers { reply, files: [{ path, content }] } and writes nothing: applying a file
    // is `saveFile` above, the same call a human save makes.
    assist: (id, messages, focus) =>
        api.post(`/admin/uapi-integrations/${id}/assistant`, { messages, focus: focus || null }),

    // ── candidates ───────────────────────────────────────────────────────────
    // Pin one x-hotelid to a version so that property exercises it while everyone else stays on live.
    listCandidates: (id) => api.get(`/admin/uapi-integrations/${id}/candidates`),
    pin: (id, data) => api.post(`/admin/uapi-integrations/${id}/candidates`, data),
    unpin: (id, candidateId) => api.delete(`/admin/uapi-integrations/${id}/candidates/${candidateId}`),
};

export default uapiIntegrationsService;
